import type { CatalogProperty } from 'src/api/generated';
import type { ExecutionStepPresentation } from 'src/modules/automations/types/executions/execution-step-presentation.type';
import { calculateDuration } from 'src/modules/automations/utils/executions/timeline/timeline-helpers.util';
import { findExecutionPropertyCode } from './execution-property-code.util';
import { formatPropertyCode } from 'src/modules/shared/utils/catalog/property-code.util';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const readString = (
  record: Record<string, unknown> | undefined,
  key: string
): string | undefined => {
  const value = record?.[key];
  return typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : undefined;
};

const readNumber = (
  record: Record<string, unknown> | undefined,
  key: string
): number | undefined => {
  const value = record?.[key];
  return typeof value === 'number' ? value : undefined;
};

const readBoolean = (
  record: Record<string, unknown> | undefined,
  key: string
): boolean | undefined => {
  const value = record?.[key];
  return typeof value === 'boolean' ? value : undefined;
};

const readTodo = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value.trim();
  }
  if (!isRecord(value)) return undefined;
  return (
    readString(value, 'content') ??
    readString(value, 'objective') ??
    readString(value, 'description')
  );
};

const getOutputs = (step: unknown): Record<string, unknown> | undefined =>
  isRecord(step) && isRecord(step.outputs) ? step.outputs : undefined;

const getNestedItems = (
  outputs: Record<string, unknown>
): unknown[] | undefined => {
  if (Array.isArray(outputs.items)) {
    return Array.from(outputs.items, (item: unknown) => item);
  }
  const result = isRecord(outputs.result) ? outputs.result : undefined;
  if (Array.isArray(result?.items)) {
    return Array.from(result.items, (item: unknown) => item);
  }
  const responseData = isRecord(outputs.responseData)
    ? outputs.responseData
    : undefined;
  return Array.isArray(responseData?.items)
    ? Array.from(responseData.items, (item: unknown) => item)
    : undefined;
};

const getError = (
  step: Record<string, unknown>,
  outputs: Record<string, unknown>
): string | undefined => {
  const outputError = outputs.error;
  if (typeof outputError === 'string') return outputError;
  if (isRecord(outputError)) return readString(outputError, 'message');
  const stepError = step.error;
  if (typeof stepError === 'string') return stepError;
  return isRecord(stepError) ? readString(stepError, 'message') : undefined;
};

const getDuration = (step: Record<string, unknown>): string | undefined => {
  const createdAt = readString(step, 'createdAt');
  const completedAt =
    readString(step, 'completedAt') ?? readString(step, 'updatedAt');
  return createdAt && completedAt
    ? calculateDuration(createdAt, completedAt)
    : undefined;
};

const getContextFacts = (
  outputs: Record<string, unknown>,
  properties: ReadonlyMap<number, CatalogProperty> | undefined
): string[] => {
  const facts: string[] = [];
  const contacts = Array.isArray(outputs.conversationContacts)
    ? outputs.conversationContacts.filter(isRecord)
    : [];
  const contact = contacts[0];
  const contactName = [
    readString(contact, 'firstName'),
    readString(contact, 'lastName'),
  ]
    .filter((value): value is string => Boolean(value))
    .join(' ');
  if (contactName) facts.push(contactName);

  const funnel = isRecord(outputs.funnel) ? outputs.funnel : undefined;
  const status = isRecord(outputs.currentFunnelStatus)
    ? outputs.currentFunnelStatus
    : undefined;
  const funnelPath = [readString(funnel, 'name'), readString(status, 'name')]
    .filter((value): value is string => Boolean(value))
    .join(' · ');
  if (funnelPath) facts.push(funnelPath);

  const property = isRecord(outputs.propertyOfInterest)
    ? outputs.propertyOfInterest
    : undefined;
  const propertyId = readNumber(property, 'id');
  const propertyLabel =
    readString(property, 'name') ??
    readString(property, 'addressDisplay') ??
    (propertyId === undefined
      ? undefined
      : formatPropertyCode({
          displayCode: findExecutionPropertyCode(properties, propertyId),
          fallback: String(propertyId),
          internalPrefix: '',
        }));
  if (propertyLabel) facts.push(propertyLabel);
  return facts;
};

const getSentMessages = (outputs: Record<string, unknown>): string[] => {
  const sentMessages = Array.isArray(outputs.sentMessages)
    ? outputs.sentMessages
    : [];
  const messages = sentMessages.flatMap((message): string[] => {
    if (typeof message === 'string' && message.trim()) return [message.trim()];
    if (!isRecord(message)) return [];
    const content = readString(message, 'content');
    return content ? [content] : [];
  });
  const directMessage =
    readString(outputs, 'message') ?? readString(outputs, 'content');
  return messages.length > 0 ? messages : directMessage ? [directMessage] : [];
};

const getUpdatedFields = (outputs: Record<string, unknown>): string[] => {
  const candidates = [outputs.updatedFields, outputs.fields, outputs.updates];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate.flatMap((field): string[] => {
        if (typeof field === 'string') return [field];
        if (!isRecord(field)) return [];
        const name =
          readString(field, 'name') ??
          readString(field, 'field') ??
          readString(field, 'key');
        return name ? [name] : [];
      });
    }
    if (isRecord(candidate)) return Object.keys(candidate);
  }
  return [];
};

/** Extracts user-facing phase and tool results without exposing raw payloads. */
const getPhasePresentation = (
  outputs: Record<string, unknown>
): Partial<ExecutionStepPresentation> => {
  const todos = Array.isArray(outputs.todos)
    ? outputs.todos
        .map(readTodo)
        .filter((todo): todo is string => Boolean(todo))
    : [];
  const executedToolsCount = Array.isArray(outputs.executedTools)
    ? outputs.executedTools.length
    : undefined;
  const objective =
    readString(outputs, 'objective') ?? readString(outputs, 'goal');
  const goalAchieved = readBoolean(outputs, 'goalAchieved');
  const reason = readString(outputs, 'reason');
  const validatorAction = readString(outputs, 'action');
  const affectedTodo = readString(outputs, 'affectedTodo');
  const nextTodo = readString(outputs, 'nextTodo');
  const remainingTodosCount = readNumber(outputs, 'remainingTodosCount');

  return {
    ...(objective ? { objective } : {}),
    todos,
    ...(goalAchieved !== undefined ? { goalAchieved } : {}),
    ...(reason ? { reason } : {}),
    ...(validatorAction ? { validatorAction } : {}),
    ...(affectedTodo ? { affectedTodo } : {}),
    ...(nextTodo ? { nextTodo } : {}),
    ...(remainingTodosCount !== undefined ? { remainingTodosCount } : {}),
    ...(executedToolsCount !== undefined ? { executedToolsCount } : {}),
  };
};

const getContextPresentation = (
  outputs: Record<string, unknown>,
  properties: ReadonlyMap<number, CatalogProperty> | undefined
): Partial<ExecutionStepPresentation> => {
  const interactions = isRecord(outputs.interactions)
    ? outputs.interactions
    : undefined;
  const contextMessagesCount = Array.isArray(interactions?.messages)
    ? interactions.messages.length
    : undefined;
  const contextCallsCount = Array.isArray(interactions?.calls)
    ? interactions.calls.length
    : undefined;

  return {
    contextFacts: getContextFacts(outputs, properties),
    ...(contextMessagesCount !== undefined ? { contextMessagesCount } : {}),
    ...(contextCallsCount !== undefined ? { contextCallsCount } : {}),
  };
};

const getSearchToolPresentation = (
  outputs: Record<string, unknown>
): Partial<ExecutionStepPresentation> => {
  const parameters = isRecord(outputs.parameters)
    ? outputs.parameters
    : undefined;
  const searchQuery =
    readString(parameters, 'search') ??
    readString(parameters, 'query') ??
    readString(outputs, 'search') ??
    readString(outputs, 'query');
  const items = getNestedItems(outputs);
  return {
    ...(searchQuery ? { searchQuery } : {}),
    ...(items ? { resultCount: items.length } : {}),
  };
};

const getMessageToolPresentation = (
  outputs: Record<string, unknown>
): Partial<ExecutionStepPresentation> => {
  const summary = isRecord(outputs.summary) ? outputs.summary : undefined;
  const attemptedMessagesCount = readNumber(summary, 'attempted');
  const failedMessagesCount = readNumber(summary, 'failed');

  return {
    sentMessages: getSentMessages(outputs),
    ...(attemptedMessagesCount !== undefined ? { attemptedMessagesCount } : {}),
    ...(failedMessagesCount !== undefined ? { failedMessagesCount } : {}),
  };
};

const getTaskToolPresentation = (
  outputs: Record<string, unknown>
): Partial<ExecutionStepPresentation> => {
  const objective = readString(outputs, 'objective');
  const taskStatus = readString(outputs, 'status');
  const taskDueDate = readString(outputs, 'dueDate');

  return {
    ...(objective ? { taskObjective: objective } : {}),
    ...(taskStatus ? { taskStatus } : {}),
    ...(taskDueDate ? { taskDueDate } : {}),
  };
};

const getFieldsToolPresentation = (
  outputs: Record<string, unknown>
): Partial<ExecutionStepPresentation> => ({
  updatedFields: getUpdatedFields(outputs),
});

const getPropertyToolPresentation = (
  outputs: Record<string, unknown>,
  properties: ReadonlyMap<number, CatalogProperty> | undefined
): Partial<ExecutionStepPresentation> => {
  const propertyId = outputs.propertyId;
  return {
    ...(typeof propertyId === 'number' || typeof propertyId === 'string'
      ? {
          propertyId: String(propertyId),
          propertyDisplayCode: findExecutionPropertyCode(
            properties,
            propertyId
          ),
        }
      : propertyId === null
        ? { propertyId: '' }
        : {}),
  };
};

const getSemanticToolPresentation = (
  toolName: string,
  outputs: Record<string, unknown>,
  properties: ReadonlyMap<number, CatalogProperty> | undefined
): Partial<ExecutionStepPresentation> => {
  if (
    toolName.includes('search-properties') ||
    toolName.includes('search-products')
  ) {
    return getSearchToolPresentation(outputs);
  }
  if (
    toolName === 'draft-and-send-message' ||
    toolName.includes('send-whatsapp')
  ) {
    return getMessageToolPresentation(outputs);
  }
  if (toolName.includes('tasks.')) {
    return getTaskToolPresentation(outputs);
  }
  if (toolName.includes('update-contact-and-conversation-fields')) {
    return getFieldsToolPresentation(outputs);
  }
  if (toolName.includes('set-conversation-property-of-interest')) {
    return getPropertyToolPresentation(outputs, properties);
  }
  return {};
};

const getToolPresentation = (
  step: Record<string, unknown>,
  outputs: Record<string, unknown>,
  properties: ReadonlyMap<number, CatalogProperty> | undefined
): Partial<ExecutionStepPresentation> => {
  const toolName = readString(step, 'toolName') ?? '';
  const error = getError(step, outputs);
  const duration = getDuration(step);
  const semanticPresentation = getSemanticToolPresentation(
    toolName,
    outputs,
    properties
  );

  return {
    ...semanticPresentation,
    ...(error ? { error } : {}),
    ...(duration ? { duration } : {}),
  };
};

/** Extracts user-facing phase and tool results without exposing raw payloads. */
export const buildExecutionStepPresentation = (
  step: unknown,
  properties?: ReadonlyMap<number, CatalogProperty>
): ExecutionStepPresentation => {
  if (!isRecord(step)) {
    return {
      todos: [],
      contextFacts: [],
      sentMessages: [],
      updatedFields: [],
    };
  }
  const outputs = getOutputs(step) ?? {};

  return {
    todos: [],
    contextFacts: [],
    sentMessages: [],
    updatedFields: [],
    ...getPhasePresentation(outputs),
    ...getContextPresentation(outputs, properties),
    ...getToolPresentation(step, outputs, properties),
  };
};

/** Whether a projected step has details worth expanding. */
export const hasExecutionStepDetails = (
  presentation: ExecutionStepPresentation
): boolean => {
  const optionalValues = [
    presentation.objective,
    presentation.goalAchieved,
    presentation.reason,
    presentation.validatorAction,
    presentation.affectedTodo,
    presentation.nextTodo,
    presentation.remainingTodosCount,
    presentation.executedToolsCount,
    presentation.contextMessagesCount,
    presentation.contextCallsCount,
    presentation.searchQuery,
    presentation.resultCount,
    presentation.attemptedMessagesCount,
    presentation.failedMessagesCount,
    presentation.taskObjective,
    presentation.taskStatus,
    presentation.taskDueDate,
    presentation.propertyId,
    presentation.error,
    presentation.duration,
  ];
  return (
    optionalValues.some((value) => value !== undefined) ||
    presentation.todos.length > 0 ||
    presentation.contextFacts.length > 0 ||
    presentation.sentMessages.length > 0 ||
    presentation.updatedFields.length > 0
  );
};
